import { useCallback, useEffect, useRef, useState } from "react";
import classNames from "classnames";
import { GrClose } from "react-icons/gr";
import LoginRegistrationForm from "components/zephr/LoginRegistrationForm";
import { LOGIN_DIALOG_ID } from "lib/zephr/constants";
import { zephrModal } from "lib/zephr/modal";
import {
  useLoginRegistrationModal,
  type LoginFormType,
} from "lib/store/loginRegistrationModal";
import { useReserveScrollbarWidth } from "lib/hooks/useReserveScrollbarWidth";
import { useSitewideOption } from "lib/store/sitewideOption";

const FORM_WAIT_TIMEOUT_MS = 1500;
const FORM_POLL_MS = 100;

const formSelector = (formType: LoginFormType) =>
  `#${LOGIN_DIALOG_ID} [data-login-form="${formType}"] form`;

const waitForForm = (selector: string): Promise<HTMLFormElement | null> =>
  new Promise((resolve) => {
    const startedAt = Date.now();

    const intervalId = window.setInterval(() => {
      const form = document.querySelector<HTMLFormElement>(selector);

      if (form) {
        window.clearInterval(intervalId);
        resolve(form);
        return;
      }

      if (Date.now() - startedAt >= FORM_WAIT_TIMEOUT_MS) {
        window.clearInterval(intervalId);
        resolve(null);
      }
    }, FORM_POLL_MS);
  });

export const LoginRegistrationModal = () => {
  const { isOpen, redirectUrl, formType, close } = useLoginRegistrationModal();
  const { sitewideOption } = useSitewideOption();
  const [formReady, setFormReady] = useState(false);
  const authCompleted = useRef(false);

  const dismiss = useCallback(() => {
    zephrModal.cancelOpenLater();
    close();
  }, [close]);

  useEffect(() => {
    if (!isOpen) {
      setFormReady(false);
      return;
    }

    let cancelled = false;

    waitForForm(formSelector(formType)).then((form) => {
      if (cancelled) {
        return;
      }

      if (!form) {
        window.location.assign(
          `/members/login?redirectUrl=${encodeURIComponent(redirectUrl)}`
        );
        return;
      }

      setFormReady(true);
    });

    return () => {
      cancelled = true;
    };
  }, [isOpen, redirectUrl, formType]);

  useEffect(() => {
    if (!formReady) {
      return;
    }

    document.body.style.overflow = "hidden";

    return () => {
      document.body.style.overflow = "";
    };
  }, [formReady]);

  useReserveScrollbarWidth({ modalOpened: formReady });

  useEffect(() => {
    if (!formReady) {
      return;
    }

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        dismiss();
      }
    };

    window.addEventListener("keydown", onKeyDown);

    return () => window.removeEventListener("keydown", onKeyDown);
  }, [formReady, dismiss]);

  const onAuthComplete = useCallback(() => {
    if (authCompleted.current) {
      return;
    }

    authCompleted.current = true;
    setFormReady(false);
    window.location.assign(redirectUrl);
  }, [redirectUrl]);

  return (
    <div
      className={classNames({ hidden: !formReady })}
      data-testid="login-modal-root"
    >
      <div
        className="fixed inset-0 z-[60] bg-[#000000a2]"
        data-testid="login-modal-overlay"
        onClick={dismiss}
      />
      <div className="pointer-events-none fixed inset-0 z-[61] flex items-center justify-center">
        <div
          id={LOGIN_DIALOG_ID}
          data-zephr-dialog
          className="pointer-events-auto relative max-h-[92vh] w-[94vw] max-w-[400px] overflow-y-auto rounded-2xl bg-white px-4 pb-6 pt-8 shadow-[0_10px_38px_-10px_rgba(14,18,22,0.35)] md:px-6"
        >
          <button
            aria-label="Close"
            data-testid="login-modal-close"
            className="absolute right-3 top-3 z-10 p-2 focus:outline-none focus-visible:ring-2 focus-visible:ring-black-rock"
            onClick={dismiss}
          >
            <GrClose size="1.2em" />
          </button>
          <div
            data-login-form="regular"
            className={classNames({ hidden: formType !== "regular" })}
          >
            <LoginRegistrationForm
              variant="modal"
              formType="regular"
              selector={formSelector("regular")}
              onAuthComplete={onAuthComplete}
              newslettersList={sitewideOption?.newslettersList ?? []}
            />
          </div>
          <div
            data-login-form="checkout"
            className={classNames({ hidden: formType !== "checkout" })}
          >
            <LoginRegistrationForm
              variant="modal"
              formType="checkout"
              selector={formSelector("checkout")}
              onAuthComplete={onAuthComplete}
              newslettersList={sitewideOption?.newslettersList ?? []}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginRegistrationModal;
